import { registerCommand } from "./registry.ts";
import { parseViewspecCodes } from "../../viewspecs/codes.ts";
import { getCurrentSID } from "../plugins/addressing.ts";
import type { ViewspecState } from "../../viewspecs/types.ts";

// --- Set Viewspec ---
registerCommand({
  name: "set viewspec",
  aliases: ["sv", "sev"],
  description: 'Set viewspec codes (e.g., "bhm" = depth 1, show SIDs, indented)',
  args: [{ name: "codes", type: "viewspec", prompt: "Viewspecs:" }],
  execute(ctx, args) {
    if (args.codes) {
      ctx.setViewspecs(parseViewspecCodes(args.codes));
    }
  },
});

// --- Zoom In ---
registerCommand({
  name: "zoom in",
  aliases: ["zi"],
  description: "Zoom into current statement (freeze on subtree)",
  args: [],
  execute(ctx) {
    const view = ctx.editorView;
    if (!view) return;
    const sid = getCurrentSID(view.state);
    if (sid) {
      ctx.setViewspecs({ showFrozen: sid });
    }
  },
});

// --- Zoom Out ---
registerCommand({
  name: "zoom out",
  aliases: ["zo"],
  description: "Zoom out (unfreeze view)",
  args: [],
  execute(ctx) {
    ctx.setViewspecs({ showFrozen: null });
  },
});

// --- Depth ---
registerCommand({
  name: "depth",
  aliases: ["dl"],
  description: 'Set depth limit (1-9 or "all")',
  args: [{ name: "level", type: "text", prompt: 'Depth (1-9 or "all"):' }],
  execute(ctx, args) {
    const level = args.level ?? "all";
    const limit =
      level === "all" || level === "a" ? null : parseInt(level, 10);
    if (limit === null || !isNaN(limit)) {
      ctx.setViewspecs({
        depthLimit: limit === null ? null : Math.max(1, Math.min(9, limit)),
      });
    }
  },
});

// --- Filter ---
registerCommand({
  name: "filter",
  aliases: ["fi"],
  description: "Show only statements containing text",
  args: [{ name: "text", type: "text", prompt: "Filter:" }],
  execute(ctx, args) {
    ctx.setViewspecs({ filterText: args.text || null });
  },
});

// --- Split ---
registerCommand({
  name: "split",
  aliases: ["sp"],
  description: "Split screen (h=horizontal, v=vertical, n=none)",
  args: [{ name: "mode", type: "text", prompt: "Mode (h/v/n):" }],
  execute(ctx, args) {
    const map: Record<string, ViewspecState["splitMode"]> = {
      h: "horizontal",
      v: "vertical",
      n: "none",
      horizontal: "horizontal",
      vertical: "vertical",
      none: "none",
    };
    const mode = args.mode?.toLowerCase() ?? "none";
    ctx.setViewspecs({ splitMode: map[mode] ?? "none" });
  },
});
